/* Copyright (c) 2026 Hyegro Pty Ltd. All rights reserved. */

/**
 * New booking — /real-estate/bookings/new[?unit_id=][&application_id=]
 *
 * With application_id (a customer-portal application, v2 Convert), the
 * wizard fills the unit, the primary buyer and the price from
 * GET /re/applications/{id}/conversion, the agent reviews and saves as
 * usual, and the saved booking is linked back (POST .../link). Without it
 * nothing here changes.
 *
 * Steps: Unit → Buyers → Price & plan → Broker → Review.
 * GET /re/units/{id}, /re/units (AVAILABLE,HELD), /re/projects/{id}, /re/payment-plans,
 * POST /re/payment-plans/preview, GET /re/commission-rules (indicative only),
 * POST /re/bookings.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert, FormControlLabel, Grid, Icon, IconButton, InputAdornment, MenuItem, Switch,
  TextField, Tooltip,
} from '@mui/material';
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import MDTypography from 'components/MDTypography';
import InfoCard from './components/ReInfoCard';
import { usePermission } from 'auth/usePermission';
import RePage, { RePanel, useSnack } from './components/RePage';
import ReStatusChip from './components/ReStatusChip';
import ReStepper from './components/ReStepper';
import {
  ContactPicker, LeadPicker, RePicker, UserPicker,
} from './components/Pickers';
import { DateField, KV } from './components/Widgets';
import SchedulePreview, { usePlanPreview } from './components/SchedulePreview';
import {
  BOOKING_PARTY_ROLES, bedroomsLabel, errorMessage, formatMoney, formatNumber, formatPct, getErrorCode, humanize,
  round2, todayIso, toNumber, useReApi, useReGet, useReOptions,
} from './api';
import { defaultCurrency, isAustralia } from '../variant';

const STEPS = ['Unit', 'Buyers', 'Price & plan', 'Broker', 'Review'];
const BUYER_ROLES = ['PRIMARY_BUYER', 'JOINT_BUYER'];

const newParty = (role = 'JOINT_BUYER') => ({
  key: Math.random().toString(36).slice(2), contact_id: null, contact: null, role, ownership_pct: '',
  emirates_id: '', passport_no: '', nationality: '',
});

function UnitStep({ unitId, setUnitId, unit, projectFilter, setProjectFilter }) {
  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={5}>
        <MDBox display="flex" flexDirection="column" gap={2}>
          <RePicker path="/re/projects" label="Project" value={projectFilter} onChange={(id) => { setProjectFilter(id); setUnitId(null); }} sx={{ width: '100%' }} />
          <RePicker
            path="/re/units"
            label="Unit (available or held)"
            value={unitId}
            onChange={setUnitId}
            params={{ project_id: projectFilter, status: 'AVAILABLE,HELD' }}
            getLabel={(u) => [u.unit_number, u.building_name, bedroomsLabel(u.bedrooms), formatMoney(u.list_price, u.currency, { decimals: 0 }), u.status === 'HELD' ? 'HELD' : null].filter(Boolean).join(' · ')}
            sx={{ width: '100%' }}
          />
        </MDBox>
      </Grid>
      <Grid item xs={12} md={7}>
        {unit ? (
          <InfoCard icon="apartment" title={`Unit ${unit.unit_number}`} headerAction={<ReStatusChip vocab="unit" status={unit.status} size="xs" />}>
            <KV label="Project" value={unit.project_name} />
            <KV label="Building / floor" value={[unit.building_name, unit.floor != null ? `Floor ${unit.floor}` : null].filter(Boolean).join(' · ') || '—'} />
            <KV label="Type" value={[unit.unit_type_name, humanize(unit.category), bedroomsLabel(unit.bedrooms)].filter(Boolean).join(' · ')} />
            <KV label="BUA" value={unit.bua_sqft ? `${formatNumber(unit.bua_sqft)} sq ft` : '—'} />
            <KV label="List price" value={formatMoney(unit.list_price, unit.currency)} bold />
            {!['AVAILABLE', 'HELD'].includes(unit.status) && (
              <Alert severity="error" sx={{ mt: 1 }}>This unit is not available for booking.</Alert>
            )}
            {unit.status === 'HELD' && (
              <Alert severity="info" sx={{ mt: 1 }}>The unit is on hold. Its active hold converts into this booking.</Alert>
            )}
          </InfoCard>
        ) : (
          <MDTypography variant="button" color="text">Pick a unit to continue.</MDTypography>
        )}
      </Grid>
    </Grid>
  );
}

function BuyersStep({ parties, setParties, notify }) {
  const update = (key, patch) => setParties((ps) => ps.map((p) => (p.key === key ? { ...p, ...patch } : p)));
  const remove = (key) => setParties((ps) => ps.filter((p) => p.key !== key));
  return (
    <MDBox display="flex" flexDirection="column" gap={2}>
      {parties.map((p, idx) => (
        <RePanel key={p.key} sx={{ bgcolor: '#FAFBFD' }}>
          <Grid container spacing={1.5} alignItems="center">
            <Grid item xs={12} md={4}>
              <ContactPicker
                label={idx === 0 ? 'Primary buyer' : 'Contact'}
                value={p.contact_id}
                onChange={(id, c) => update(p.key, { contact_id: id, contact: c })}
                allowCreate
                onNotify={notify}
                sx={{ width: '100%' }}
                required
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField
                select size="small" fullWidth label="Role" value={p.role}
                disabled={idx === 0}
                onChange={(e) => update(p.key, { role: e.target.value })}
              >
                {BOOKING_PARTY_ROLES.filter((r) => idx === 0 || r !== 'PRIMARY_BUYER').map((r) => <MenuItem key={r} value={r}>{humanize(r)}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={6} md={1.5}>
              <TextField
                size="small" fullWidth type="number" label="Ownership" value={p.ownership_pct}
                disabled={!BUYER_ROLES.includes(p.role)}
                onChange={(e) => update(p.key, { ownership_pct: e.target.value })}
                InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
              />
            </Grid>
            {!isAustralia() && (
              <Grid item xs={12} md={1.5}>
                <TextField size="small" fullWidth label="Emirates ID" value={p.emirates_id} onChange={(e) => update(p.key, { emirates_id: e.target.value })} />
              </Grid>
            )}
            <Grid item xs={6} md={1.25}>
              <TextField size="small" fullWidth label="Passport" value={p.passport_no} onChange={(e) => update(p.key, { passport_no: e.target.value })} />
            </Grid>
            <Grid item xs={5} md={1.25}>
              <TextField size="small" fullWidth label="Nationality" value={p.nationality} onChange={(e) => update(p.key, { nationality: e.target.value })} />
            </Grid>
            <Grid item xs={1} md={0.5}>
              {idx > 0 && (
                <Tooltip title="Remove">
                  <IconButton size="small" onClick={() => remove(p.key)}><Icon>delete_outline</Icon></IconButton>
                </Tooltip>
              )}
            </Grid>
          </Grid>
        </RePanel>
      ))}
      <MDBox display="flex" gap={1}>
        <MDButton variant="outlined" color="info" size="small" onClick={() => setParties((ps) => [...ps, newParty('JOINT_BUYER')])}>
          <Icon>person_add</Icon>&nbsp;Add joint buyer
        </MDButton>
        <MDButton variant="text" color="info" size="small" onClick={() => setParties((ps) => [...ps, newParty('POA')])}>
          Add POA / guarantor / nominee
        </MDButton>
      </MDBox>
    </MDBox>
  );
}

function IndicativeCommission({ firmId, projectId, netPrice }) {
  const rules = useReOptions(firmId ? '/re/commission-rules' : null, {
    applies_to: 'SALE', is_active: true, broker_firm_id: firmId,
  });
  const rule = useMemo(() => {
    const candidates = rules.options.filter((r) => (!r.project_id || r.project_id === projectId)
      && (!r.broker_firm_id || r.broker_firm_id === firmId) && r.payee_type !== 'INTERNAL_AGENT');
    return candidates.sort((a, b) => (b.priority || 0) - (a.priority || 0)
      || (b.project_id ? 1 : 0) - (a.project_id ? 1 : 0)
      || (b.broker_firm_id ? 1 : 0) - (a.broker_firm_id ? 1 : 0))[0] || null;
  }, [rules.options, projectId, firmId]);
  if (!firmId || !rule) return null;
  let gross = null;
  if (rule.basis === 'PERCENT_OF_NET_PRICE' && rule.rate_pct != null) gross = round2((netPrice * Number(rule.rate_pct)) / 100);
  if (rule.basis === 'FIXED_AMOUNT' && rule.fixed_amount != null) gross = Number(rule.fixed_amount);
  if (gross === null) return null;
  return (
    <Alert severity="info" sx={{ mt: 2 }}>
      {`Indicative commission under "${rule.name}": ${formatMoney(gross)}`}
      {rule.rate_pct != null ? ` (${formatPct(rule.rate_pct, 2)} of net price)` : ''}
      {rule.apply_vat ? ' + 5% VAT' : ''}
      {Number(rule.eligible_after_paid_pct) > 0 ? `, payable once the buyer has paid ${formatPct(rule.eligible_after_paid_pct, 0)}` : ''}
      . The booking accrues the actual entries from the active rules when it is saved.
    </Alert>
  );
}

export default function BookingWizard() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { api } = useReApi();
  const [snack, notify] = useSnack();
  const { canCreate } = usePermission('re_bookings');
  const [step, setStep] = useState(0);
  const [stepError, setStepError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const [unitId, setUnitId] = useState(searchParams.get('unit_id') ? Number(searchParams.get('unit_id')) : null);
  const applicationId = searchParams.get('application_id') ? Number(searchParams.get('application_id')) : null;
  const conversion = useReGet(applicationId ? `/re/applications/${applicationId}/conversion` : null);
  const prefill = conversion.data?.target === 'booking' ? conversion.data.prefill : null;
  const [projectFilter, setProjectFilter] = useState(null);
  const unitReq = useReGet(unitId ? `/re/units/${unitId}` : null);
  const unit = unitId ? unitReq.data : null;
  const projectReq = useReGet(unit?.project_id ? `/re/projects/${unit.project_id}` : null);
  const project = projectReq.data;

  const [parties, setParties] = useState([newParty('PRIMARY_BUYER')]);
  const [price, setPrice] = useState({
    booking_date: todayIso(), list_price: '', discount_amount: '0', dld_fee_waived: false, admin_fee_amount: '', oqood_fee_amount: '',
    // Australia: contract of sale
    deposit_pct: '10', transfer_duty_estimate: '', settlement_date: '',
  });
  const au = isAustralia();
  const [planId, setPlanId] = useState(null);
  const [broker, setBroker] = useState({ broker_firm_id: null, broker_agent_id: null });
  const [meta, setMeta] = useState({ assigned_to: null, lead_id: searchParams.get('lead_id') ? Number(searchParams.get('lead_id')) : null, source: '', notes: '' });

  // From a portal application: the unit, the applicant as primary buyer, their offer and deposit.
  useEffect(() => {
    if (!prefill) return;
    if (prefill.unit_id) setUnitId((cur) => cur || prefill.unit_id);
    if (prefill.contact_id) {
      setParties((ps) => ps.map((p, i) => (i === 0 && !p.contact_id ? { ...p, contact_id: prefill.contact_id } : p)));
      // The review step names the buyer from the contact record.
      api.get(`/contacts/${prefill.contact_id}`)
        .then((res) => setParties((ps) => ps.map((p) => (p.contact_id === prefill.contact_id && !p.contact ? { ...p, contact: res.data } : p))))
        .catch(() => {});
    }
    if (prefill.deposit_pct != null) setPrice((p) => ({ ...p, deposit_pct: String(prefill.deposit_pct) }));
  }, [prefill, api]);

  // Prefill price from the unit (or the application's offer), fees and default plan from the project.
  useEffect(() => {
    const offered = prefill?.list_price != null ? String(prefill.list_price) : null;
    if (unit) setPrice((p) => ({ ...p, list_price: offered ?? (unit.list_price != null ? String(unit.list_price) : '') }));
  }, [unit, prefill]);
  useEffect(() => {
    if (project) {
      setPrice((p) => ({
        ...p,
        admin_fee_amount: p.admin_fee_amount || (project.admin_fee_amount != null ? String(project.admin_fee_amount) : ''),
        oqood_fee_amount: p.oqood_fee_amount || (project.oqood_fee_amount != null ? String(project.oqood_fee_amount) : ''),
      }));
      if (project.default_payment_plan_id) setPlanId((cur) => cur || project.default_payment_plan_id);
    }
  }, [project]);

  const plans = useReOptions('/re/payment-plans', { is_active: true });
  const planOptions = useMemo(
    () => plans.options.filter((pl) => pl.is_active !== false && (!pl.project_id || !unit || pl.project_id === unit.project_id)),
    [plans.options, unit],
  );

  const currency = unit?.currency || defaultCurrency();
  const listPrice = toNumber(price.list_price) || 0;
  const discount = toNumber(price.discount_amount) || 0;
  const netPrice = round2(listPrice - discount);
  // No DLD or Oqood outside the UAE; Australia records transfer duty itself.
  const dldPct = au ? 0 : (toNumber(project?.dld_fee_pct) ?? 4);
  const dldFee = round2((netPrice * dldPct) / 100);
  const adminFee = toNumber(price.admin_fee_amount) || 0;
  const oqoodFee = au ? 0 : (toNumber(price.oqood_fee_amount) || 0);
  const depositAmount = round2((netPrice * (toNumber(price.deposit_pct) || 0)) / 100);
  const transferDuty = toNumber(price.transfer_duty_estimate) || 0;
  const buyerUpfront = au
    ? round2(depositAmount + adminFee)
    : round2((price.dld_fee_waived ? 0 : dldFee) + adminFee + oqoodFee);

  const preview = usePlanPreview(planId && netPrice > 0 ? {
    payment_plan_id: planId, net_price: netPrice, booking_date: price.booking_date, project_id: unit?.project_id,
  } : null);

  const validate = (s) => {
    if (s === 0) {
      if (!unit) return 'Choose a unit.';
      if (!['AVAILABLE', 'HELD'].includes(unit.status)) return `Unit ${unit.unit_number} is ${humanize(unit.status)} and cannot be booked.`;
    }
    if (s === 1) {
      if (parties.some((p) => !p.contact_id)) return 'Every party needs a contact.';
      const seen = new Set();
      for (let i = 0; i < parties.length; i += 1) {
        const k = `${parties[i].contact_id}|${parties[i].role}`;
        if (seen.has(k)) return 'The same contact is listed twice with the same role.';
        seen.add(k);
      }
      const buyers = parties.filter((p) => BUYER_ROLES.includes(p.role));
      if (buyers.length > 1 || buyers.some((b) => b.ownership_pct !== '')) {
        const sum = buyers.reduce((a, b) => a + (toNumber(b.ownership_pct) || 0), 0);
        if (Math.abs(sum - 100) > 0.01) return `Buyer ownership must total 100% (currently ${round2(sum)}%).`;
      }
    }
    if (s === 2) {
      if (!price.booking_date) return 'Booking date is required.';
      if (listPrice <= 0) return 'List price must be greater than zero.';
      if (discount < 0 || discount >= listPrice) return 'Discount must be zero or more and less than the list price.';
      if (!planId) return 'Choose a payment plan.';
    }
    return null;
  };

  const next = () => {
    const e = validate(step);
    setStepError(e);
    if (!e) setStep((s) => s + 1);
  };

  const back = () => {
    setStepError(null);
    setStep((s) => Math.max(0, s - 1));
  };

  const submit = async () => {
    for (let s = 0; s < 3; s += 1) {
      const e = validate(s);
      if (e) {
        setStep(s);
        setStepError(e);
        return;
      }
    }
    setSubmitting(true);
    setStepError(null);
    const buyers = parties.filter((p) => BUYER_ROLES.includes(p.role));
    const body = {
      unit_id: unit.id,
      payment_plan_id: planId,
      booking_date: price.booking_date,
      list_price: listPrice,
      discount_amount: discount,
      net_price: netPrice,
      dld_fee_waived: price.dld_fee_waived,
      admin_fee_amount: toNumber(price.admin_fee_amount),
      oqood_fee_amount: toNumber(price.oqood_fee_amount),
      ...(au ? {
        deposit_pct: toNumber(price.deposit_pct),
        transfer_duty_estimate: toNumber(price.transfer_duty_estimate),
        settlement_date: price.settlement_date || null,
      } : {}),
      parties: parties.map((p) => ({
        contact_id: p.contact_id,
        role: p.role,
        ownership_pct: BUYER_ROLES.includes(p.role)
          ? (toNumber(p.ownership_pct) ?? (buyers.length === 1 ? 100 : null))
          : null,
        emirates_id: p.emirates_id || null,
        passport_no: p.passport_no || null,
        nationality: p.nationality || null,
      })),
      broker_firm_id: broker.broker_firm_id || null,
      broker_agent_id: broker.broker_agent_id || null,
      lead_id: meta.lead_id || null,
      assigned_to: meta.assigned_to || null,
      source: meta.source || null,
      notes: meta.notes || null,
    };
    try {
      const res = await api.post('/re/bookings', body);
      if (applicationId) {
        try {
          await api.post(`/re/applications/${applicationId}/link`, { booking_id: res.data.id });
        } catch (linkErr) {
          // The booking is saved; the application page offers to link it again.
          navigate(`/real-estate/applications/${applicationId}`, {
            replace: true,
            state: { linkError: errorMessage(linkErr, 'The application could not be marked converted.'), bookingId: res.data.id },
          });
          return;
        }
      }
      navigate(`/real-estate/bookings/${res.data.id}`, { replace: true });
    } catch (e) {
      const code = getErrorCode(e);
      setStepError(errorMessage(e, 'Could not create the booking'));
      if (code === 'UNIT_NOT_AVAILABLE') {
        setStep(0);
        unitReq.reload();
      } else if (code === 'PLAN_PERCENTAGES_MUST_TOTAL_100') {
        setStep(2);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const setP = (k) => (e) => setPrice((p) => ({ ...p, [k]: e && e.target ? e.target.value : e }));

  if (!canCreate) {
    return (
      <RePage title="New booking" onBack={() => navigate('/real-estate/bookings')}>
        <Alert severity="warning">You do not have permission to create bookings.</Alert>
      </RePage>
    );
  }

  const primary = parties[0];

  return (
    <RePage title="New booking" subtitle={unit ? `Unit ${unit.unit_number} · ${unit.project_name || ''}` : 'Reserve a unit for a buyer'} onBack={() => navigate(-1)}>
      <RePanel sx={{ mb: 2 }}>
        <ReStepper steps={STEPS} activeStep={step} />
      </RePanel>

      {applicationId && (
        <Alert severity={conversion.error ? 'error' : 'info'} sx={{ mb: 2 }}>
          {conversion.error
            ? errorMessage(conversion.error, 'The application could not be loaded.')
            : 'Filled in from the customer\'s application. Check every step, then create the booking; the application is marked converted when you save.'}
        </Alert>
      )}
      {stepError && <Alert severity="error" sx={{ mb: 2 }}>{stepError}</Alert>}

      <RePanel sx={{ mb: 2 }} p={2.5}>
        {step === 0 && (
          <UnitStep unitId={unitId} setUnitId={setUnitId} unit={unit} projectFilter={projectFilter} setProjectFilter={setProjectFilter} />
        )}

        {step === 1 && <BuyersStep parties={parties} setParties={setParties} notify={notify} />}

        {step === 2 && (
          <Grid container spacing={3}>
            <Grid item xs={12} md={5}>
              <MDBox display="flex" flexDirection="column" gap={2}>
                <DateField label="Booking date" value={price.booking_date} onChange={setP('booking_date')} required />
                <TextField size="small" type="number" label={`List price (${currency})`} value={price.list_price} onChange={setP('list_price')} />
                <TextField size="small" type="number" label={`Discount (${currency})`} value={price.discount_amount} onChange={setP('discount_amount')} />
                {au ? (
                  <>
                    <Grid container spacing={1.5}>
                      <Grid item xs={6}><TextField fullWidth size="small" type="number" label="Deposit %" value={price.deposit_pct} onChange={setP('deposit_pct')} /></Grid>
                      <Grid item xs={6}><TextField fullWidth size="small" type="number" label={`Transfer duty estimate (${currency})`} value={price.transfer_duty_estimate} onChange={setP('transfer_duty_estimate')} helperText="Varies by state; not calculated" /></Grid>
                    </Grid>
                    <DateField label="Settlement date" value={price.settlement_date} onChange={setP('settlement_date')} />
                  </>
                ) : (
                  <>
                    <FormControlLabel
                      control={<Switch checked={price.dld_fee_waived} onChange={(e) => setPrice((p) => ({ ...p, dld_fee_waived: e.target.checked }))} />}
                      label={`DLD fee (${formatPct(dldPct, 0)}) waived by developer`}
                    />
                    <Grid container spacing={1.5}>
                      <Grid item xs={6}><TextField fullWidth size="small" type="number" label="Admin fee" value={price.admin_fee_amount} onChange={setP('admin_fee_amount')} /></Grid>
                      <Grid item xs={6}><TextField fullWidth size="small" type="number" label="Oqood fee" value={price.oqood_fee_amount} onChange={setP('oqood_fee_amount')} /></Grid>
                    </Grid>
                  </>
                )}
                <TextField
                  select size="small" label="Payment plan" value={planId || ''}
                  onChange={(e) => setPlanId(e.target.value ? Number(e.target.value) : null)}
                  helperText={plans.loading ? 'Loading plans…' : (!planOptions.length ? 'No active payment plans. Create one first.' : null)}
                >
                  {planOptions.map((pl) => (
                    <MenuItem key={pl.id} value={pl.id}>{`${pl.name}${pl.project_id ? '' : ' (global)'}`}</MenuItem>
                  ))}
                </TextField>
              </MDBox>
            </Grid>
            <Grid item xs={12} md={7}>
              <InfoCard icon="calculate" title="Price breakdown">
                <KV label="List price" value={formatMoney(listPrice, currency)} />
                <KV label="Discount" value={discount ? `− ${formatMoney(discount, currency)}` : formatMoney(0, currency)} />
                <KV label="Net price" value={formatMoney(netPrice, currency)} bold />
                {au ? (
                  <>
                    <KV label={`Deposit ${formatPct(price.deposit_pct, 0)}`} value={formatMoney(depositAmount, currency)} />
                    <KV label="Transfer duty (estimate, paid at settlement)" value={formatMoney(transferDuty, currency)} />
                    <KV label="Buyer pays on exchange" value={formatMoney(buyerUpfront, currency)} bold />
                  </>
                ) : (
                  <>
                    <KV
                      label={`DLD fee ${formatPct(dldPct, 0)}${price.dld_fee_waived ? ' (developer pays)' : ''}`}
                      value={formatMoney(dldFee, currency)}
                      color={price.dld_fee_waived ? '#8A5300' : null}
                    />
                    <KV label="Admin fee" value={formatMoney(adminFee, currency)} />
                    <KV label="Oqood fee" value={formatMoney(oqoodFee, currency)} />
                    <KV label="Buyer pays upfront in fees" value={formatMoney(buyerUpfront, currency)} bold />
                  </>
                )}
              </InfoCard>
              <MDBox mt={2}>
                <MDTypography variant="button" fontWeight="bold">Installment schedule preview</MDTypography>
                <SchedulePreview {...preview} currency={currency} />
              </MDBox>
            </Grid>
          </Grid>
        )}

        {step === 3 && (
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <MDBox display="flex" flexDirection="column" gap={2}>
                <MDTypography variant="button" color="text">Optional. Leave empty for a direct sale.</MDTypography>
                <RePicker
                  path="/re/broker-firms"
                  label="Broker firm"
                  value={broker.broker_firm_id}
                  onChange={(id) => setBroker({ broker_firm_id: id, broker_agent_id: null })}
                  getLabel={(f) => [f.name, f.rera_orn ? `ORN ${f.rera_orn}` : null].filter(Boolean).join(' · ')}
                  sx={{ width: '100%' }}
                />
                <RePicker
                  path="/re/broker-agents"
                  label="Broker agent"
                  value={broker.broker_agent_id}
                  onChange={(id) => setBroker((b) => ({ ...b, broker_agent_id: id }))}
                  params={{ firm_id: broker.broker_firm_id }}
                  disabled={!broker.broker_firm_id}
                  getLabel={(a) => [a.name, a.brn ? `BRN ${a.brn}` : null].filter(Boolean).join(' · ')}
                  sx={{ width: '100%' }}
                />
                <IndicativeCommission firmId={broker.broker_firm_id} projectId={unit?.project_id} netPrice={netPrice} />
              </MDBox>
            </Grid>
            <Grid item xs={12} md={6}>
              <MDBox display="flex" flexDirection="column" gap={2}>
                <UserPicker label="Sales agent (assigned to)" value={meta.assigned_to} onChange={(v) => setMeta((m) => ({ ...m, assigned_to: v }))} emptyLabel="Not set" sx={{ width: '100%' }} />
                <LeadPicker label="Lead (optional)" value={meta.lead_id} onChange={(v) => setMeta((m) => ({ ...m, lead_id: v }))} sx={{ width: '100%' }} />
                <TextField size="small" label="Source" value={meta.source} onChange={(e) => setMeta((m) => ({ ...m, source: e.target.value }))} placeholder={au ? 'Walk-in, realestate.com.au, Domain, referral…' : 'Walk-in, Bayut, Property Finder, referral…'} />
                <TextField size="small" multiline minRows={3} label="Notes" value={meta.notes} onChange={(e) => setMeta((m) => ({ ...m, notes: e.target.value }))} />
              </MDBox>
            </Grid>
          </Grid>
        )}

        {step === 4 && unit && (
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <InfoCard icon="apartment" title="Unit">
                <KV label="Unit" value={`${unit.unit_number} · ${unit.project_name || ''}`} />
                <KV label="Type" value={[bedroomsLabel(unit.bedrooms), humanize(unit.category)].join(' · ')} />
                <KV label="Status now" value={humanize(unit.status)} />
              </InfoCard>
              <MDBox mt={2}>
                <InfoCard icon="groups" title="Parties">
                  {parties.map((p) => (
                    <KV
                      key={p.key}
                      label={humanize(p.role)}
                      value={`${p.contact ? [p.contact.first_name, p.contact.last_name].filter(Boolean).join(' ') || p.contact.email : `#${p.contact_id}`}${p.ownership_pct ? ` · ${p.ownership_pct}%` : ''}`}
                    />
                  ))}
                </InfoCard>
              </MDBox>
            </Grid>
            <Grid item xs={12} md={6}>
              <InfoCard icon="payments" title="Price">
                <KV label="Booking date" value={price.booking_date} />
                <KV label="Net price" value={formatMoney(netPrice, currency)} bold />
                {au ? (
                  <>
                    <KV label="Deposit" value={formatMoney(depositAmount, currency)} />
                    <KV label="Transfer duty (estimate)" value={formatMoney(transferDuty, currency)} />
                  </>
                ) : (
                  <>
                    <KV label="DLD fee" value={`${formatMoney(dldFee, currency)}${price.dld_fee_waived ? ' (waived)' : ''}`} />
                    <KV label="Admin + Oqood" value={formatMoney(adminFee + oqoodFee, currency)} />
                  </>
                )}
                <KV label="Payment plan" value={planOptions.find((pl) => pl.id === planId)?.name || `#${planId}`} />
                <KV label="Installments" value={preview.lines.length || '—'} />
                <KV label="Broker" value={broker.broker_firm_id ? 'Yes' : 'Direct sale'} />
              </InfoCard>
            </Grid>
            <Grid item xs={12}>
              <Alert severity="info">
                {`Confirming reserves unit ${unit.unit_number} for ${primary.contact ? [primary.contact.first_name, primary.contact.last_name].filter(Boolean).join(' ') : 'the buyer'}, builds the installment schedule and accrues broker commission.`}
              </Alert>
            </Grid>
          </Grid>
        )}
      </RePanel>

      <MDBox display="flex" justifyContent="space-between">
        <MDButton variant="outlined" color="secondary" onClick={step === 0 ? () => navigate(-1) : back} disabled={submitting}>
          {step === 0 ? 'Cancel' : 'Back'}
        </MDButton>
        {step < STEPS.length - 1 ? (
          <MDButton variant="gradient" color="info" onClick={next} disabled={step === 0 && unitReq.loading}>Next</MDButton>
        ) : (
          <MDButton variant="gradient" color="success" onClick={submit} disabled={submitting}>
            {submitting ? 'Booking…' : 'Confirm booking'}
          </MDButton>
        )}
      </MDBox>
      {snack}
    </RePage>
  );
}
